import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

interface Row {
  k: ReactNode;
  v: ReactNode;
}

function Table({ rows }: { rows: Row[] }) {
  return (
    <div className="dp-fields-table" style={{ marginTop: 18 }}>
      {rows.map((row, i) => (
        <div className="dp-field-row" key={i}>
          <div className="dp-field-k">{row.k}</div>
          <div className="dp-field-v">{row.v}</div>
        </div>
      ))}
    </div>
  );
}

const code = (text: string) => <span className="api-inline">{text}</span>;

const CHOOSE: Row[] = [
  {
    k: 'Two records for one customer',
    v: 'Merge the duplicate into the one you keep (section 02). Irreversible.',
  },
  {
    k: 'A client that should not exist',
    v: 'Delete the client (section 03). Irreversible, and it removes the invoices too.',
  },
  {
    k: 'A subscription created by mistake',
    v: 'Delete the subscription and choose what happens to its invoices (section 04). For a normal ending, end the subscription instead.',
  },
  {
    k: 'A whole import that went wrong',
    v: 'Roll back the import batch (section 05). This is an API call, not a dashboard button.',
  },
  { k: 'Two sources disagree about one invoice', v: 'Resolve the discrepancy (section 07).' },
];

const MOVES: Row[] = [
  {
    k: 'Moved to the kept client',
    v: 'Subscriptions, invoices, contacts, emails, payment reminders, pricing rules, renewal actions, feature flags and their history, contracts, and the lifecycle, version, price override and billing timing history of those subscriptions.',
  },
  {
    k: 'Field conflicts',
    v: 'You choose per field, source or target, for Name, Description, Billing Address, Tax ID, Invoice Prefix, Payment Terms (days), Region, Linked Tenant and Incubator ID. Fields that already match are not listed.',
  },
  {
    k: 'Contact conflicts',
    v: 'Contacts with the same email on both clients. You pick which one to keep and whether to combine their labels. The other is deleted.',
  },
  {
    k: 'Feature flag conflicts',
    v: 'A flag set on both clients. You choose whether it stays on or off.',
  },
  {
    k: 'Carried over when the kept client has none',
    v: 'Invoice prefix, external id and Google Drive folder. The removed client’s invoices keep their numbers.',
  },
  {
    k: 'Not moved',
    v: 'The merge does not move CRM notes, documents or meetings. They go with the removed client, so copy anything you need first.',
  },
];

export function FixYourDataGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Merge, delete and undo — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Merge, delete and undo</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Merge, delete and <em>undo</em>.
        </h1>
        <p className="dp-oneliner">
          How to clean up duplicate clients, remove records created by mistake, roll back a bad
          import, bring in invoice history from a spreadsheet, and settle invoices that two sources
          disagree about. Most of these cannot be undone, so each section says what is removed.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          Pick the right tool
        </h2>
        <Table rows={CHOOSE} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          None of the actions below asks for an admin role. They need you to be signed in to the
          workspace. Importing invoices from Google Drive or a CSV is covered in{' '}
          <Link to="/docs/guides/import-invoices">Import your invoice history</Link>.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Merge two clients
        </h2>
        <UiPath
          steps={['Clients', 'Merge clients']}
          note="Or open the client to remove, use the actions menu and choose Merge. A company record has the same menu item."
        />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          The merge is a four-step wizard. <strong>Select clients</strong>: search for the client to
          keep (Keep, Target) and the one to remove (Remove, Source). They cannot be the same.{' '}
          <strong>Resolve conflicts</strong>: pick the value to keep for each field that differs.{' '}
          <strong>Review data</strong>: see how many subscriptions, contacts, invoices and other
          records will move, and settle contact and feature flag conflicts.{' '}
          <strong>Confirm merge</strong>: give a reason (up to 500 characters) and type the removed
          client’s name exactly. The merge cannot be undone.
        </p>
        <Figure
          title="The merge wizard"
          caption="Three steps are reversible while you work; the last one is not."
        >
          <Flow
            steps={[
              { title: 'Select clients', text: 'The one to keep and the one to remove.' },
              { title: 'Resolve conflicts', text: 'Pick the value to keep per differing field.' },
              { title: 'Review data', text: 'See what moves; settle contact and flag conflicts.' },
              {
                title: 'Confirm merge',
                text: 'Give a reason and type the removed name. Cannot be undone.',
                tone: 'danger',
              },
            ]}
          />
        </Figure>
        <Table rows={MOVES} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          <strong>Learned aliases.</strong> After the merge, the removed client’s name is stored as
          an alias of the one you kept, and any aliases it already had are moved across. Later
          imports that carry the old name land on the kept client, checked right after external id
          and invoice prefix. Recording the alias never makes the merge fail.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Delete a client
        </h2>
        <UiPath steps={['Clients', 'open the client', 'actions menu', 'Delete']} />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Give a reason and type the client’s name to confirm. There is no soft delete, and a client
          with active subscriptions can be deleted. Revvly removes the client’s CRM data first, then
          its subscriptions and their usage, usage events, expansions, lifecycle events,
          subscription-level prices and API key scopes, then its invoices, pricing rules, renewal
          actions, contacts, feature flags, emails, payment reminders and contracts, and finally the
          client. Pipelines are workspace settings and are not touched. A client created in the
          master workspace may own a linked workspace; that is cleaned up too, conservatively, so
          the name can be reused.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Invoices are deleted outright, with no void step. If the client has real billing history,
          prefer ending its subscriptions.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Delete a subscription
        </h2>
        <UiPath
          steps={['Subscriptions', 'open a subscription', 'actions menu', 'Delete']}
          note="To end a live subscription normally, use End subscription in the same menu."
        />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          This is for mistakes such as the wrong client or test data. The subscription is removed
          with its usage counters and events, expansions, renewal actions, subscription-level prices
          and API key scopes, and its lifecycle events, so it no longer appears in past MRR.
          Billing-period snapshots and the usage audit trail are kept. The modal asks what to do
          with <strong>Related invoices</strong>:
        </p>
        <Table
          rows={[
            { k: 'Leave invoices unchanged', v: 'The default. The invoices stay as they are.' },
            {
              k: 'Void invoices',
              v: 'Marks every invoice of the subscription Cancelled and keeps the record for audit. That includes invoices already paid, so use credit notes where money was collected.',
            },
            {
              k: 'Delete invoices permanently',
              v: 'Removes the invoices and their lines. Use only to correct a mistake.',
            },
          ]}
        />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          Roll back an import batch
        </h2>
        <p className="dp-plain">
          Every transaction import, subscription import and Google Drive commit is recorded as a
          batch, and everything it creates is stamped with the batch id. Rolling back is available
          only through the GraphQL API as {code('rollbackImportBatch(batchId)')}. The dashboard has
          no screen for it and does not display the batch id; the id is returned as{' '}
          {code('importBatchId')} in the result of the import call.
        </p>
        <Table
          rows={[
            {
              k: 'Deleted',
              v: 'The clients, subscriptions and invoices that batch created, with their lifecycle events and their version, price override and billing timing history.',
            },
            {
              k: 'Never touched',
              v: 'Anything that existed before, or that the batch only matched or extended. A created client is removed only if it has no other subscriptions or invoices by then.',
            },
            {
              k: 'Put back',
              v: 'A pre-existing invoice the batch linked to a subscription it created is unlinked, so it returns to the Unmatched tab.',
            },
            { k: 'Repeat', v: 'A batch can be rolled back once. A second call is refused.' },
            {
              k: 'Result',
              v: 'Counts for invoices deleted and unlinked, subscriptions, clients, lifecycle events and history records.',
            },
          ]}
        />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Contacts and companies imports are different: the CRM import page keeps a batch history,
          and opening a batch offers <strong>Revert batch</strong> in the dashboard.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          Import invoice lines from a spreadsheet
        </h2>
        <UiPath
          steps={['Finance', 'Import', 'Transaction Import', 'Start Import']}
          note="The same hub is under Settings, Import. No feature flag is needed."
        />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Use it when you have invoice-level rows. It creates clients that are missing, creates or
          extends subscriptions from the billing periods, and creates the invoices with their
          payment status and dates. The wizard has six steps:
        </p>
        <Table
          rows={[
            { k: '1. Upload File', v: 'CSV, XLS or XLSX, up to 10 MB.' },
            {
              k: '2. Map Columns',
              v: 'The columns are read by AI and suggested; a banner says whether AI or the built-in rules did it. Client Name, Amount and Invoice Date are required. Optional: Contact Email, Invoice Number, Currency, Due Date, Expected Payment Date, Payment Date, Payment Status, Period Start and End Date, Description, Notes, Region. A switch identifies clients by invoice prefix instead of name.',
            },
            {
              k: '3. Preview',
              v: 'Per-row validation: missing values, bad amounts, bad dates and a negative amount warning.',
            },
            {
              k: '4. Analysis',
              v: 'Clients to create and to update, potential duplicates, active versus expired subscriptions, and the billing periods inferred. Blocking issues must be fixed; warnings must be reviewed or dismissed. Export CSV saves the issue list.',
            },
            {
              k: '5. Plan',
              v: 'Optionally apply one bundle or set of modules to every subscription. Skip it and the product is matched per row.',
            },
            {
              k: '6. Complete',
              v: 'Counts imported and failed, with the error for each failed row, and how many existing invoices were marked paid from their payment date.',
            },
          ]}
        />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          <strong>Period inference.</strong> A row with no period end gets a period (monthly,
          quarterly or yearly) read from its description by AI, for example “Annual maintenance” or
          “Q1 service fee”, with keyword rules as the fallback. Repeated descriptions are cached.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          <strong>Product inference.</strong> Without a bundle or modules on the row or in the Plan
          step, each subscription is matched to your catalog: first a stored answer, then a learned
          rule, then AI. If nothing fits it is marked unmatched for you to assign.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          <strong>Settling invoices already in Revvly.</strong> When a row names an invoice Revvly
          already holds, such as one imported from a PDF or a client folder without a payment date,
          and carries a Payment Date, the import records the payment on that invoice. A part payment
          books only what is missing, and importing the same file again records nothing twice. A row
          marked Paid with no payment date leaves the invoice unpaid. Invoice files from{' '}
          <Link to="/docs/guides/drive-folders">client folders in Google Drive</Link>, and email
          attachments marked Invoice, go through the Google Drive import review instead.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          <strong>What it learns.</strong> The import reads two memories. Client aliases come from
          merges and from corrections made in the Google Drive review queue. Product rules are saved
          when a reviewer there moves an invoice to another product, keyed on the normalised
          description, so the same wording matches next time without AI. A one-time answer, or a
          save that leaves the product unchanged, writes no rule. Rows are processed oldest period
          first, so the result does not depend on how the file is sorted. Undo with section 05.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          Review invoice discrepancies
        </h2>
        <UiPath steps={['Finance', 'Discrepancies']} note="The tab badge counts pending items." />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          When the same invoice number arrives from a second source, such as a spreadsheet import or
          a Drive re-sync, Revvly does not overwrite it. It compares number, subscription, client,
          amount, currency, invoice date, due date, expected payment date, payment terms, type and
          notes, and records a discrepancy. Payment status and payment date are ignored.
        </p>
        <Table
          rows={[
            {
              k: 'Single',
              v: 'Open a row to see both sides. Conflicting fields have a choice per field, defaulting to Source 1, which is the PDF for Drive invoices. Resolve writes only the chosen values for the conflicting fields.',
            },
            {
              k: 'Bulk',
              v: 'Accept All PDF Values resolves every pending discrepancy with Source 1. The API also accepts Source 2. It applies to all pending items at once, so clear any you want to decide by hand first.',
            },
            {
              k: 'Failures',
              v: 'In a bulk run, a discrepancy whose invoice is gone or has no applicable field is reported and left pending.',
            },
          ]}
        />
        <ul className="dp-connects" style={{ marginTop: 18 }}>
          <li>
            <Link
              to="/docs/guides/collect-payments"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Collect what you are owed ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/import-invoices"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Import your invoice history ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/guides/drive-folders"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Client folders in Google Drive ›
            </Link>
          </li>
          <li>
            <Link to="/docs/concepts/client" style={{ color: 'inherit', textDecoration: 'none' }}>
              Client concept ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
